import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import { CustomButton } from '../Button/Button';
import CloseButton from '../CloseButton/CloseButton';
import Icon from '../Icon/Icon';
import styles from './BackgroundMusicEditor.module.scss';
import cx from 'classnames';
import SingleTrack from './SingleTrack';
import Slider from 'rc-slider';
import { useTranslation } from 'react-i18next';
import RadioButton from '../RadioButton/RadioButton';
import { useUserContext } from '../../context/UserContext';
import ResourceHelper from '../../utils/ResourceHelper';
import TooltipButton from '../TooltipButton/TooltipButton';

const BackgroundMusicEditor = ({ activeScene, currentVideoPart, onBackgroundMusicChange, miniSidebar, closeFeatures, onEnhanceScene, onRemoveEnhanceScene }) => {
  const {
    selectedMusic,
    availableMusic,
    playerPlayedMusic,
    previewPlayedMusic,
    chooseBackgroundMusic,
    disableBackgroundMusic,
    musicVolume,
    videoVolume,
    changeMusicVolume,
    changeVideoVolume,
    handlePreviewPlayedMusic,
    isApplyAllTrackEnabled,
    isApplyAllVolumeEnabled,
    toggleApplyAllTrack,
    toggleApplyAllVolume,
  } = useBackgroundMusicContext();

  const [initialMusic, setInitialMusic] = useState(selectedMusic);
  const [initialMusicVolume, setInitialMusicVolume] = useState(musicVolume);
  const [initialVideoVolume, setInitialVideoVolume] = useState(videoVolume);

  const { isAudioEnhanceEnabled } = useUserContext();

  const initialIsApplyAllTrackEnabled = useMemo(() => isApplyAllTrackEnabled, []);
  const initialIsApplyAllVolumeEnabled = useMemo(() => isApplyAllVolumeEnabled, []);

  const [isChangeInitialValues, setIsChangeInitialValues] = useState(false);
  const { t } = useTranslation();

  const isNoChanges = useMemo(
    () =>
      selectedMusic?.id === initialMusic?.id &&
      musicVolume === initialMusicVolume &&
      videoVolume === initialVideoVolume &&
      isApplyAllTrackEnabled === initialIsApplyAllTrackEnabled &&
      isApplyAllVolumeEnabled === initialIsApplyAllVolumeEnabled,
    [
      selectedMusic,
      initialMusic,
      musicVolume,
      initialMusicVolume,
      videoVolume,
      initialVideoVolume,
      isApplyAllTrackEnabled,
      initialIsApplyAllTrackEnabled,
      isApplyAllVolumeEnabled,
      initialIsApplyAllVolumeEnabled,
    ]
  );

  const handleMuteUnmuteVideo = () => {
    const defaultVideoVolume = 100;
    const newVideoVolumeValue = videoVolume === 0 ? defaultVideoVolume : 0;

    changeVideoVolume(newVideoVolumeValue);

    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: selectedMusic,
      musicVolume: musicVolume / 100,
      videoVolume: newVideoVolumeValue / 100,
      isApplyAllTrackEnabled: false,
      isApplyAllVolumeEnabled: false,
      isTemporaryAction: true,
    });
  };

  useEffect(() => {
    if (miniSidebar || closeFeatures) {
      handleClose();
    }
  }, [miniSidebar, closeFeatures]);

  const handleMuteUnmuteMusic = () => {
    const defaultMusicVolume = 50;
    const newMusicVolumeValue = musicVolume === 0 ? defaultMusicVolume : 0;

    changeMusicVolume(musicVolume === 0 ? defaultMusicVolume : 0);

    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: selectedMusic,
      musicVolume: newMusicVolumeValue / 100,
      videoVolume: videoVolume / 100,
      isApplyAllTrackEnabled: false,
      isApplyAllVolumeEnabled: false,
      isTemporaryAction: true,
    });
  };

  const isSceneAudioEnhanced = useMemo(() => {
    return ResourceHelper.getResourceByLabel(activeScene, 'enhanced')?.type === 'normalized' || activeScene.enhancingDone;
  }, [activeScene]);

  const enhancingDisabledMessage = useMemo(() => {
    if (ResourceHelper.getResourceByLabel(activeScene, 'avatar')?.type === 'normalized') {
      return t('my_video.tools.enhance.avatar_video');
    }
    if (isSceneAudioEnhanced && !activeScene.enhancing) {
      return t('my_video.tools.enhance.already_normalized');
    }
    if (activeScene.hasOwnProperty('originalId')) {
      return t('my_video.tools.enhance.save_changes');
    }
    return '';
  }, [activeScene])

  const handleSaveChanges = () => {
    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: selectedMusic,
      musicVolume: musicVolume / 100,
      videoVolume: videoVolume / 100,
      isApplyAllTrackEnabled,
      isApplyAllVolumeEnabled,
    });

    disableBackgroundMusic();
  };

  const handleClose = () => {
    chooseBackgroundMusic(initialMusic);
    changeMusicVolume(initialMusicVolume);
    changeVideoVolume(initialVideoVolume);

    disableBackgroundMusic();

    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: initialMusic,
      musicVolume: initialMusicVolume / 100,
      videoVolume: initialVideoVolume / 100,
      isApplyAllTrackEnabled: false,
      isApplyAllVolumeEnabled: false,
      isTemporaryAction: true,
    });
  };

  const handleSelectNoMusic = () => {
    chooseBackgroundMusic(null);

    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: null,
      musicVolume: musicVolume / 100,
      videoVolume: videoVolume / 100,
      isApplyAllTrackEnabled: false,
      isApplyAllVolumeEnabled: false,
      isTemporaryAction: true,
    });
  };

  const handleSelectVideoVolume = value => {
    changeVideoVolume(value);

    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: selectedMusic,
      musicVolume: musicVolume / 100,
      videoVolume: value / 100,
      isApplyAllTrackEnabled: false,
      isApplyAllVolumeEnabled: false,
      isTemporaryAction: true,
    });
  };

  const handleSelectMusicVolume = value => {
    changeMusicVolume(value);

    onBackgroundMusicChange({
      sceneIndex: Number(currentVideoPart),
      newMusic: selectedMusic,
      musicVolume: value / 100,
      videoVolume: videoVolume / 100,
      isApplyAllTrackEnabled: false,
      isApplyAllVolumeEnabled: false,
      isTemporaryAction: true,
    });
  };

  const updateInitialValues = useCallback(() => {
    setInitialMusic(selectedMusic);
    setInitialMusicVolume(musicVolume);
    setInitialVideoVolume(videoVolume);

    setIsChangeInitialValues(false);
  }, [selectedMusic, musicVolume, videoVolume]);

  useEffect(() => {
    // When user change the currentVideoPart the change of the selected music is triggered
    // Therefore, we need to wait a bit to pick up the new correct initial values for the new scene
    setTimeout(() => setIsChangeInitialValues(true), 100);
  }, [currentVideoPart]);

  useEffect(() => {
    if (isChangeInitialValues) {
      updateInitialValues();
    }
  }, [isChangeInitialValues]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.optionsContainer}>
        <div className={styles.headerContainer}>
          <h2 className={styles.textHeader}>{t('my_video.tools.audio')}</h2>
          <CloseButton onClose={handleClose} />
        </div>
        {isAudioEnhanceEnabled && <div className={styles.enhanceContainer}>
          <span className={styles.tracksTitle}>{t('my_video.tools.enhance.title')}</span>
          {isSceneAudioEnhanced && !activeScene.enhancing ? (
            <TooltipButton
              classNameButton={styles.enhanceButton}
              variant='secondary'
              label={t('my_video.tools.remove_enhance.button')}
              onClick={() => onRemoveEnhanceScene(activeScene.id)}
              loading={activeScene.enhancing}
              loadingSpinner={true}
              loadingText={t('my_video.tools.enhance.processing')}
            />
          ) : (
            <TooltipButton
              classNameButton={styles.enhanceButton}
              variant='secondary'
              label={t('my_video.tools.enhance.button')}
              onClick={() => onEnhanceScene(activeScene.id)}
              loading={activeScene.enhancing}
              loadingSpinner={true}
              loadingText={t('my_video.tools.enhance.processing')}
              disabled={activeScene.enhancing || enhancingDisabledMessage.length}
              tooltipText={enhancingDisabledMessage}
              hoverOnDisabled={true}
            />
          )}
        </div>}
        <div className={styles.tracksTitleContainer}>
          <span className={styles.tracksTitle}>{t('my_video.tools.audio.tracks')}</span>

          <RadioButton
            id={'applyTrack'}
            checked={isApplyAllTrackEnabled}
            onChange={toggleApplyAllTrack}
            label={t('my_video.tools.audio.apply_to_all')}
            variant={'secondary'}
            className={styles.radioButton}
          />
        </div>
        <div className={cx(styles.allMusicContainer)}>
          <div
            className={cx(styles.musicContainer, { [styles.musicContainerSelected]: !selectedMusic })}
            onClick={handleSelectNoMusic}
          >
            <div className={styles.emptyImageContainer}>
              <Icon src="noMusic" className={styles.actionIcon} verticalAlign="middle" />
            </div>
            <div className={styles.musicInfoContainer}>
              <span className={styles.musicName}>{t('my_video.tools.audio.no_music')}</span>
            </div>
          </div>
          {availableMusic?.map(track => (
            <SingleTrack
              key={track.id}
              track={track}
              selectedMusic={selectedMusic}
              currentVideoPart={currentVideoPart}
              playerPlayedMusic={playerPlayedMusic}
              previewPlayedMusic={previewPlayedMusic}
              handlePreviewPlayedMusic={handlePreviewPlayedMusic}
              musicVolume={musicVolume}
              videoVolume={videoVolume}
              chooseBackgroundMusic={chooseBackgroundMusic}
              onBackgroundMusicChange={onBackgroundMusicChange}
            />
          ))}
        </div>
        <div className={styles.volumeTextContainer}>
          <span className={styles.volumeText}>{t('my_video.tools.audio.volume')}</span>

          <RadioButton
            id={'applyVolume'}
            checked={isApplyAllVolumeEnabled}
            onChange={toggleApplyAllVolume}
            label={t('my_video.tools.audio.apply_volumne_to_all')}
            variant={'secondary'}
            className={styles.radioButton}
          />
        </div>
        <div className={styles.volumeContainer}>
          <div className={styles.volumeValueContainer}>
            <Icon
              onClick={handleMuteUnmuteVideo}
              src={videoVolume === 0 ? 'noVideoVolume' : 'videoVolume'}
              className={styles.icon}
              verticalAlign="middle"
            />
            <span className={styles.volumeValue}>{Math.round(videoVolume)}</span>
          </div>
          <Slider
            min={0}
            max={125}
            step={1}
            onChange={handleSelectVideoVolume}
            value={videoVolume}
            included
            style={{ width: '160px', paddingRight: '8px', zIndex: 100 }}
            handleStyle={{
              width: '18px',
              height: '18px',
              background: '#7C43AA',
              position: 'relative',
              top: '-10px',
              borderRadius: '9px',
              boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
            }}
            trackStyle={{
              background: '#7C43AA',
              height: '4px',
              borderRadius: '2px',
              position: 'relative',
              top: '-4px',
            }}
            railStyle={{
              background: '#EEEEEE',
              height: '4px',
              borderRadius: '2px',
              position: 'relative',
            }}
          />
        </div>

        <div className={cx(styles.volumeContainer, { [styles.disabled]: !selectedMusic })}>
          <div className={styles.volumeValueContainer}>
            <Icon
              onClick={handleMuteUnmuteMusic}
              src={musicVolume === 0 ? 'noMusicVolume' : 'musicVolume'}
              className={styles.icon}
              verticalAlign="middle"
            />
            <span className={styles.volumeValue}>{Math.round(musicVolume)}</span>
          </div>
          <Slider
            min={0}
            max={100}
            step={1}
            onChange={handleSelectMusicVolume}
            value={musicVolume}
            included
            style={{ width: '160px', paddingRight: '8px', zIndex: 100 }}
            handleStyle={{
              width: '18px',
              height: '18px',
              background: '#7C43AA',
              position: 'relative',
              top: '-10px',
              borderRadius: '9px',
              boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
            }}
            trackStyle={{
              background: '#7C43AA',
              height: '4px',
              borderRadius: '2px',
              position: 'relative',
              top: '-4px',
            }}
            railStyle={{
              background: '#EEEEEE',
              height: '4px',
              borderRadius: '2px',
              position: 'relative',
            }}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <CustomButton
          onClick={handleSaveChanges}
          disabled={isNoChanges}
          className={styles.saveBtn}
          label={t('general.save')}
          variant="primary"
        />
      </div>
    </div>
  );
};

export default BackgroundMusicEditor;
